'use client';

import React from 'react';
import { Input, Button, Tooltip } from '@heroui/react';
import {
  FilterSelect,
  ModelSearchSelect,
  FilterOption,
  DateFilterSelect,
  ResponsiveTooltip,
} from '../../../../components/ui';
import { useTranslations } from '../../../../contexts/translation.context';
import { SearchInputIc, ResetFiltersIc, AccountIcon } from '../../../../components/icons';
import {
  getPeriodOptions,
  getTypeOptions,
  getStatusOptions,
  getCampaignTypeOptions,
} from '../constants';
import { FiltersProps } from '../types';

const FiltersSection: React.FC<FiltersProps> = ({
  filters,
  localSearch,
  availableModels,
  availableCampaigns,
  isLoading,
  hasActiveFilters,
  onSearchChange,
  onFilterChange,
  onResetFilters,
  onModelSearchChange,
  onLoadChart,
}) => {
  const { t } = useTranslations();

  const modelOptions: FilterOption[] = availableModels.map((model) => ({
    key: model.id,
    avatar: model.avatar,
    username: model.username,
    name: model.name,
    label: `${model.name} (@${model.username})`,
  }));

  const handleDateRangeChange = (value: string) => {
    onFilterChange('dateRange', value);
    if (value !== 'custom') {
      onFilterChange('customStartDate', null);
      onFilterChange('customEndDate', null);
    }
  };

  const handleCustomDateChange = (startDate: string, endDate: string) => {
    onFilterChange('customStartDate', startDate);
    onFilterChange('customEndDate', endDate);
    onFilterChange('dateRange', 'custom');
  };

  return (
    <div className="flex items-center justify-between w-full">
      <div className="flex items-center gap-4 md:flex-wrap max-md:h-10 [&>*]:shrink-0 max-md:overflow-x-auto max-md:px-4 max-md:-mx-4 scroller-none">
        <Input
          type="text"
          placeholder={t('partner.revshare.transactions.searchPlaceholder')}
          size="sm"
          value={localSearch}
          onChange={(e) => onSearchChange(e.target.value)}
          endContent={<SearchInputIc />}
          classNames={{
            mainWrapper: 'w-[17.0625rem]',
            base: 'w-[17.0625rem]',
            input: 'text-sm text-auth-ink placeholder:text-neutral-700',
            inputWrapper:
              'rounded-2 h-9 w-[17.0625rem] bg-auth-surface border-none shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]',
            innerWrapper: 'bg-transparent',
          }}
        />
        <ModelSearchSelect
          placeholder={t('partner.revshare.transactions.filters.account') || 'Аккаунт'}
          options={modelOptions}
          selectedKey={filters.account}
          onSelectionChange={(value) => onFilterChange('account', value)}
          onSearchChange={onModelSearchChange || (() => {})}
          isLoading={isLoading}
          startContent={<AccountIcon className="text-neutral-8 stroke-neutral-8" size={20} />}
          variant="campaign"
          autoWidth={true}
          t={t}
        />
        <FilterSelect
          placeholder={t('partner.revshare.transactions.filters.campaign')}
          options={getCampaignTypeOptions(t)}
          selectedKey={filters.campaign}
          variant="campaign"
          onSelectionChange={(value) => onFilterChange('campaign', value === 'all' ? '' : value)}
          startContent={
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1.66602 10.0013C1.66602 6.07293 1.66602 4.10875 2.8864 2.88836C4.10679 1.66797 6.07098 1.66797 9.99935 1.66797C13.9277 1.66797 15.8919 1.66797 17.1123 2.88836C18.3327 4.10875 18.3327 6.07293 18.3327 10.0013C18.3327 13.9297 18.3327 15.8939 17.1123 17.1142C15.8919 18.3346 13.9277 18.3346 9.99935 18.3346C6.07098 18.3346 4.10679 18.3346 2.8864 17.1142C1.66602 15.8939 1.66602 13.9297 1.66602 10.0013Z"
                stroke="#A8A8B0"
              />
              <path d="M5.83398 15V7.5" stroke="#A8A8B0" strokeLinecap="round" />
              <path d="M10 15V5" stroke="#A8A8B0" strokeLinecap="round" />
              <path d="M14.166 14.9987V10.832" stroke="#A8A8B0" strokeLinecap="round" />
            </svg>
          }
          autoWidth={true}
        />
        {/* <FilterSelect
          placeholder={t('partner.revshare.transactions.filters.period')}
          options={getPeriodOptions(t)}
          selectedKey={filters.period}
          variant="campaign"
          onSelectionChange={(value) => onFilterChange('period', value)}
          startContent={
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 5V10L13.3333 11.6667M18.3333 10C18.3333 14.6024 14.6024 18.3333 10 18.3333C5.39763 18.3333 1.66666 14.6024 1.66666 10C1.66666 5.39763 5.39763 1.66667 10 1.66667C14.6024 1.66667 18.3333 5.39763 18.3333 10Z" stroke="#A8A8B0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          }
          autoWidth={true}
        /> */}
        <FilterSelect
          placeholder={t('partner.revshare.transactions.filters.type')}
          options={getTypeOptions(t)}
          selectedKey={filters.type}
          variant="campaign"
          onSelectionChange={(value) => onFilterChange('type', value === 'all' ? '' : value)}
          startContent={
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect
                x="2.5"
                y="2.5"
                width="6.25"
                height="6.25"
                rx="1.5"
                stroke="#A8A8B0"
                strokeWidth="1.5"
              />
              <rect
                x="11.25"
                y="2.5"
                width="6.25"
                height="6.25"
                rx="1.5"
                stroke="#A8A8B0"
                strokeWidth="1.5"
              />
              <rect
                x="2.5"
                y="11.25"
                width="6.25"
                height="6.25"
                rx="1.5"
                stroke="#A8A8B0"
                strokeWidth="1.5"
              />
              <rect
                x="11.25"
                y="11.25"
                width="6.25"
                height="6.25"
                rx="1.5"
                stroke="#A8A8B0"
                strokeWidth="1.5"
              />
            </svg>
          }
          autoWidth={true}
        />
        <DateFilterSelect
          placeholder={t('partner.revshare.transactions.filters.datePeriod') || 'Период'}
          selectedKey={filters.dateRange}
          onSelectionChange={handleDateRangeChange}
          onCustomDateChange={handleCustomDateChange}
          customStartDate={filters.customStartDate || undefined}
          customEndDate={filters.customEndDate || undefined}
          variant="campaign"
          theme="partner"
          startContent={
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M9.99935 2.29297C5.74215 2.29297 2.29102 5.74411 2.29102 10.0013C2.29102 14.2585 5.74215 17.7096 9.99935 17.7096C14.2565 17.7096 17.7077 14.2585 17.7077 10.0013C17.7077 5.74411 14.2565 2.29297 9.99935 2.29297ZM1.04102 10.0013C1.04102 5.05375 5.0518 1.04297 9.99935 1.04297C14.9469 1.04297 18.9577 5.05375 18.9577 10.0013C18.9577 14.9489 14.9469 18.9596 9.99935 18.9596C5.0518 18.9596 1.04102 14.9489 1.04102 10.0013ZM9.99935 6.04297C10.3445 6.04297 10.6243 6.32279 10.6243 6.66797V9.74242L12.5246 11.6427C12.7687 11.8868 12.7687 12.2825 12.5246 12.5266C12.2805 12.7707 11.8848 12.7707 11.6407 12.5266L9.55741 10.4432C9.4402 10.326 9.37435 10.1671 9.37435 10.0013V6.66797C9.37435 6.32279 9.65417 6.04297 9.99935 6.04297Z"
                fill="#A8A8B0"
              />
            </svg>
          }
        />
        <FilterSelect
          placeholder={t('partner.revshare.transactions.filters.status')}
          options={getStatusOptions(t)}
          selectedKey={filters.status}
          variant="campaign"
          onSelectionChange={(value) => onFilterChange('status', value === 'all' ? '' : value)}
          startContent={
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4.7233 8.26327L7.27509 4.81074C8.92455 2.57903 9.74929 1.46318 10.5188 1.69898C11.2882 1.93478 11.2882 3.30338 11.2882 6.04057V6.29865C11.2882 7.2859 11.2882 7.77952 11.6037 8.08915L11.6204 8.10519C11.9426 8.40827 12.4564 8.40827 13.4839 8.40827C15.333 8.40827 16.2575 8.40827 16.57 8.96906C16.5751 8.97834 16.5802 8.9877 16.585 8.99713C16.88 9.5666 16.3447 10.2909 15.2741 11.7394L12.7223 15.1919C11.0728 17.4236 10.248 18.5394 9.47856 18.3036C8.7091 18.0678 8.70912 16.6992 8.70916 13.962L8.70916 13.704C8.70917 12.7168 8.70918 12.2231 8.39373 11.9135L8.37704 11.8975C8.05478 11.5944 7.54102 11.5944 6.51349 11.5944C4.66443 11.5944 3.7399 11.5944 3.42745 11.0336C3.42227 11.0243 3.41724 11.0149 3.41236 11.0055C3.1174 10.436 3.6527 9.71178 4.7233 8.26327Z" stroke="#A8A8B0"/>
            </svg>
          }
          autoWidth={true}
          popoverWidth={320}
        />
        {!hasActiveFilters && <div className="shrink-0 w-10 h-2 md:hidden"></div>}
        {hasActiveFilters && (
          <Tooltip content={t('partner.revshare.offers.filters.resetTooltip')} placement="top">
            <Button
              //@ts-ignore
              size="xs"
              className="h-9 rounded-2 !w-9 p-0 bg-auth-surface shrink-0"
              onPress={onResetFilters}
            >
              <ResetFiltersIc />
            </Button>
          </Tooltip>
        )}
      </div>
      {/* {onLoadChart && (
        <Button
          color="primary"
          className="h-10 rounded-3 font-semibold"
          onPress={onLoadChart}
        >
          {t('partner.revshare.transactions.loadChart')}
        </Button>
      )} */}
    </div>
  );
};

export default FiltersSection;
